import Vue from 'vue'
import { reactive, computed, watch, toRefs } from '@vue/composition-api'
import { transform, pick, isEqual, isObject, isArray, cloneDeep } from 'lodash'


export function convertErrors(errors) {
  const convertedErrors = {}

  if (Array.isArray(errors)) {
    errors.forEach(function(element) {
      if (element.field && element.message) {
        if (!convertedErrors[element.field]) {
          convertedErrors[element.field] = []
        }
        convertedErrors[element.field].push(element.message)
      }
    })
  }

  return convertedErrors
}

export function convertSubErrors(errors) {
  const convertedErrors = {}

  if (Array.isArray(errors)) {
    errors.forEach(function(subErrors) {
      Object.keys(subErrors).forEach(field => {
        if (!convertedErrors[field]) {
          convertedErrors[field] = []
        }
        convertedErrors[field].push(Array.isArray(subErrors[field]) ? subErrors[field][0] : subErrors[field])
      })
    })
  }

  return convertedErrors
}


export function createModel(client, resource, options = {}) {

  const state = reactive({
    isNewRecord: true,
    attributes: {},
    oldAttributes: {},
    errors: {},
    isFinding: false,
    isCreating: false,
    isUpdating: false,
    isSaving: false,
  })

  const isBusy = computed(() => state.isFinding || state.isCreating || state.isUpdating || state.isSaving)

  const internal = {
    path: null,
    params: options.params || {},
    primaryKey: options.primaryKey || ['id'],
    attributes: {},
    debug: options.debug || false,
    afterFinds: [],
    afterSaves: [],
  }

  function debug() {
    internal.debug && console.log(internal.path || getPath(), ...arguments)
  }

  function getPrimaryKey() {
    const values = []

    internal.primaryKey.forEach(name => {
      values.push(state.attributes[name])
    })

    return values
  }

  function getPath(ids = []) {
    return resource + (ids.length ? '/' + ids.join(',') : '')
  }
 
  // find
  function find() {

    state.isFinding = true
    internal.path = getPath(Array.from(arguments))

    debug('find', ...arguments)

    clear()

    return client.get(internal.path, { params: internal.params })
      .then(response => {
        populate(response.data)
        state.isNewRecord = false
        internal.afterFinds.forEach(cb => { cb(state.attributes) })
        return Promise.resolve(state.attributes)
      })
      .catch(error => {
        return Promise.reject(error)
      })
      .finally(() => {
        state.isFinding = false
      })

  }

  // afterfind
  function afterFind(cb) {
    internal.afterFinds.push(cb)
    return this
  }

  // create
  function create(names = null) {

    state.isCreating = true
    state.errors = {}

    internal.path = getPath()
    //const attributes = getDirtyAttributes(names)
    const attributes = Array.isArray(names) ? getAttributes(names) : getDirtyAttributes()

    debug('create', attributes)
    
    return client.post(internal.path, attributes, { params: internal.params })
      .then(response => {
        populate(response.data, Object.keys(attributes))
        state.isNewRecord = false
        internal.afterSaves.forEach(cb => { cb(state.attributes, true, attributes) })
        return Promise.resolve(state.attributes)
      })
      .catch(error => {
        if (error.response.status === 422) {
          state.errors = convertErrors(error.response.data)
        }
        return Promise.reject(error)
      })
      .finally(() => {
        state.isCreating = false
      })

  }

  // update
  function update(names = null) {

    state.isUpdating = true
    state.errors = {}

    internal.path = getPath(getPrimaryKey())
    //const attributes = getDirtyAttributes(names)
    const attributes = Array.isArray(names) ? getAttributes(names) : getDirtyAttributes()

    debug('update', attributes)

    if (Object.keys(attributes).length === 0) {
      state.isUpdating = false
      internal.afterSaves.forEach(cb => { cb(state.attributes, false, attributes) })
      return Promise.resolve(state.attributes)
    }

    return client.put(internal.path, attributes, { params: internal.params })
      .then(response => {
        populate(response.data, Object.keys(attributes))
        state.isNewRecord = false
        internal.afterSaves.forEach(cb => { cb(state.attributes, false, attributes) })
        return Promise.resolve(state.attributes)
      })
      .catch(error => {
        if (error.response.status === 422) {
          state.errors = convertErrors(error.response.data)
        }
        return Promise.reject(error)
      })
      .finally(() => {
        state.isUpdating = false
      })

  }

  function save(names = null) {

    state.isSaving = true

    debug('save')

    return (state.isNewRecord ? create(names) : update(names)).finally(() => {
      state.isSaving = false
    })

  }

  function afterSave(cb) {
    internal.afterSaves.push(cb)
    return this
  }

  // populate
  function populate(attributes, names = null) {

    const dirtyAttributes = getDirtyAttributes()
    const dirtyNames = Object.keys(dirtyAttributes)

    names = Array.isArray(names)
      ? Object.keys(attributes).filter(name => names.includes(name))
      : Object.keys(attributes)

    Object.keys(attributes).forEach(name => {
      if (names.includes(name) || !dirtyNames.includes(name)) {
        Vue.set(state.attributes, name, cloneDeep(attributes[name]))
        //state.attributes[name] = cloneDeep(attributes[name])
      }
      internal.attributes[name] = cloneDeep(attributes[name])
      Vue.set(state.oldAttributes, name, cloneDeep(attributes[name]))
    })

  }

  function setAttribute(name, value) {
    Vue.set(state.attributes, name, value)
    return this
  }

  function setAttributes(attributes) {
    Object.keys(attributes).forEach(name => {
      setAttribute(name, attributes[name])
    })
    return this
  }

  function getAttribute(name, defaultValue = null) {
    return Object.keys(state.attributes).includes(name) ? state.attributes[name] : defaultValue
  }

  function setOldAttribute(name, value) {
    Vue.set(state.oldAttributes, name, value)
    return this
  }

  function setOldAttributes(attributes) {
    Object.keys(attributes).forEach(name => {
      setOldAttribute(name, attributes[name])
    })
    return this
  }

  function getOldAttribute(name, defaultValue = null) {
    return Object.keys(state.oldAttributes).includes(name) ? state.oldAttributes[name] : defaultValue
  }

  function getJsonAttribute(name, defaultValue = null) {
    let value = getAttribute(name, null)

    if (value === null) {
      return defaultValue
    }

    try {
      value = JSON.parse(value)
    } catch(err) {
      value = defaultValue
    }

    return value
  }

  function setJsonAttribute(name, value) {
    setAttribute(name, JSON.stringify(value))
    return this
  }

  function getAttributes(names) {

    names = Array.isArray(names)
      ? Object.keys(state.attributes).filter(name => names.includes(name))
      : Object.keys(state.attributes)
    
    return pick(state.attributes, names)
  }

  function getOldAttributes(names) {

    names = Array.isArray(names)
      ? Object.keys(state.oldAttributes).filter(name => names.includes(name))
      : Object.keys(state.oldAttributes)
    
    return pick(state.oldAttributes, names)
  }

  function hasErrors(name) {
    return name ? Object.keys(state.errors).includes(name) : Object.keys(state.errors).length
  }


  function difference(object, base) {
    return transform(object, (result, value, key) => {
      if (!isEqual(value, base[key])) {
        result[key] = isObject(value) && isObject(base[key]) && !isArray(value) ? difference(value, base[key]) : value
      }
    })
  }

  // dirty attributes
  function _getDirtyAttributes(names = null) {

    names = Array.isArray(names)
      ? Object.keys(state.attributes).filter(name => names.includes(name))
      : Object.keys(state.attributes)

    const dirtyAttributes = difference(state.attributes, internal.attributes)

    return pick(dirtyAttributes, names)
  }

  function getDirtyAttributes() {
    return difference(state.attributes, internal.attributes)
  }

  function refresh() {
    return internal.path ? find(...getPrimaryKey()) : Promise.reject()
  }

  function reset(names = null) {

    const all = (names === null)

    names = Array.isArray(names)
      ? Object.keys(internal.attributes).filter(name => names.includes(name))
      : Object.keys(internal.attributes)
    
    names.forEach(name => {
      Vue.set(state.attributes, name, cloneDeep(internal.attributes[name]))
      //state.attributes[name] = cloneDeep(internal.attributes[name])
      if (!all) {
        Vue.delete(state.errors, name)
      }
    })

    if (all) {
      state.errors = {}
    }
    
    return this
  }

  function clear() {
    state.attributes = {}
    state.errors = {}
    internal.attributes = {}
    state.oldAttributes = {}
    state.isNewRecord = true
    return this
  }


  function onReady(cb) {
    if (state.isFinding) {
      const unWatchIsFinding = watch(() => state.isFinding, val => {
        if (!val) {
          cb(state.attributes)
          unWatchIsFinding()
        }
      })
    } else {
      cb(state.attributes)
    }
  }


  return {
    ...toRefs(state),
    isBusy,
    find,
    create,
    update,
    save,
    refresh,
    reset,
    clear,
    setAttribute,
    setAttributes,
    getAttribute,
    getAttributes,
    setJsonAttribute,
    getJsonAttribute,
    setOldAttribute,
    setOldAttributes,
    getOldAttribute,
    getOldAttributes,
    hasErrors,
    onReady,
    afterFind,
    afterSave,
    getPrimaryKey,
  }



}